<template>
	<div v-if="searchOpen" class="bg-white w-full z-10 top-0 absolute">
		<div class="max-w-4xl h-11 w-full relative mx-auto">
			<svg-vue icon="search-md" class="w-5 h-5 md:w-5 md:h-5 overflow-visible absolute left-3 md:left-0 top-2" />
			<input
				ref="inputRef"
				id="product-search"
				type="text"
				autocapitalize="off"
				autocomplete="off"
				autocorrect="off"
				maxlength="64"
				spellcheck="false"
				placeholder="Search"
				class="!p-0 !ps-10 md:!ps-7 focus:!ring-0 font-normal !border-none text-sm !h-full"
				v-model="query"
				@input="refineSearch()"
			/>
			<label
				for="product-search"
				class="hidden invisible absolute left-1/2 top-1/2 -translate-y-1/2 -translate-x-1/2 text-sm font-normal"
			>
				Search
			</label>
			<button
				v-if="query.length > 0"
				type="button"
				@click="handleSearchClear()"
				class="absolute text-xs/[18px] top-3 right-10 md:right-5 hover:underline"
				aria-label="Clear Search"
			>
				Clear
			</button>
			<button
				type="button"
				@click="handleSearchClose()"
				class="absolute top-4 right-4 md:right-0"
				aria-label="Close Search"
			>
				<svg-vue icon="close-md" class="w-2.5 h-2.5 overflow-visible" />
			</button>
		</div>
		<div v-show="results.length > 0" class="overflow-y-scroll no-scrollbar max-h-[383px] !pt-4 !pb-8 w-full bg-white">
			<ul class="!px-3 !m-0 flex flex-col gap-3 max-w-4xl w-full mx-auto">
				<li v-for="item in results" :key="item.link">
					<search-hit :item="item" @list-add="handleAddToLists" />
				</li>
			</ul>
		</div>
		<generic-modal ref="modalRef" title="Add to List">
			<template v-slot:content>
				<product-list-choice
					:item="selectedItem"
					@completed="handleAddToListsComplete"
					@create-new-start="handleHideParent"
					@create-new-end="handleShowParent"
				/>
			</template>
		</generic-modal>
	</div>
</template>
<script setup>
import { ref, onMounted, onUnmounted, nextTick, watch } from 'vue'
import { storeToRefs } from 'pinia'
import axios from 'axios'

import { useSessionStore, useAppStore } from '@stores'
import SearchHit from './SearchHit.vue'

const SEARCH_ENDPOINT = import.meta.env.VITE_SEARCH_ENDPOINT
const SEARCH_TOKEN = import.meta.env.VITE_MEILISEARCH_CLIENT_KEY

const config = {
	baseURL: `${SEARCH_ENDPOINT}/indexes`,
	headers: {
		Authorization: `Bearer ${SEARCH_TOKEN}`,
		'Content-Type': 'application/json',
		Accepts: 'application/json',
	},
}
const searchClient = axios.create(config)

const sessionStore = useSessionStore()
const { itemSearchIndex } = storeToRefs(sessionStore)
const appStore = useAppStore()
const { searchOpen } = storeToRefs(appStore)
const { closeSearch } = appStore

const inputRef = ref(null)
const modalRef = ref(null)
const query = ref('')
const limit = ref(500)
const debounce = ref(null)
const results = ref([])
const selectedItem = ref(null)

onMounted(() => window.addEventListener('keydown', handleKeydown))
onUnmounted(() => window.removeEventListener('keydown', handleKeydown))

watch(searchOpen, (hasOpened) => {
	if (hasOpened) {
		nextTick(() => inputRef.value.focus())
	}
})

const handleKeydown = (e) => {
	const key = e.which || e.keyCode
	const KEY_CODES = {
		ESC: 27,
	}
	switch (key) {
		case KEY_CODES.ESC:
			handleSearchClose()
			break
		default:
			break
	}
}
const refineSearch = () => {
	clearTimeout(debounce.value)
	debounce.value = setTimeout(async () => {
		try {
			if (query.value.trim().length === 0) {
				results.value = []
				return
			}
			const url = `${itemSearchIndex.value}/search`
			const { data } = await searchClient.get(url, {
				params: {
					q: query.value,
					limit: limit.value,
					filter: 'isActive = "true" AND isSearchable = "true" AND showOnWeb = "true"',
				},
			})
			results.value = data?.hits ?? []
		} catch (_) {
			results.value = []
		}
	}, 250)
}
const handleSearchClear = () => {
	query.value = ''
	results.value = []
}
const handleSearchClose = () => {
	if (searchOpen.value) {
		handleSearchClear()
		closeSearch()
	}
}
const handleAddToLists = (item) => {
	const { exigoItemId, itemId } = item
	selectedItem.value = { ...item, id: itemId, itemId: exigoItemId }
	modalRef.value.openModal()
}
const handleAddToListsComplete = () => {
	selectedItem.value = null
	modalRef.value.closeModal()
}
const handleHideParent = () => {
	modalRef.value.dynamicHide()
	modalRef.value.enableIsNested()
}
const handleShowParent = () => {
	modalRef.value.openModal()
	modalRef.value.dynamicShow()
	modalRef.value.disableIsNested()
}
</script>
